import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { WEBHOOK_EVENT_GROUPS, type WebhookEventWire } from "@/lib/webhook-events";

type WebhookSubscriptionResponse = components["schemas"]["WebhookSubscriptionResponse"];

interface EditWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: WebhookSubscriptionResponse | null;
  onSaved: () => void;
}

export function EditWebhookDialog({ open, onOpenChange, subscription, onSaved }: EditWebhookDialogProps) {
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState<Set<WebhookEventWire>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subscription || !open) return;
    setUrl(subscription.url);
    setSelected(new Set(subscription.eventTypes.map(String) as WebhookEventWire[]));
    setSubmitting(false);
    setError(null);
  }, [subscription, open]);

  function toggle(event: WebhookEventWire) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  }

  function toggleGroup(events: WebhookEventWire[]) {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = events.every((e) => next.has(e));
      if (allSelected) events.forEach((e) => next.delete(e));
      else events.forEach((e) => next.add(e));
      return next;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subscription?.id) return;
    setError(null);

    if (!url.trim()) {
      setError("Endpoint URL is required.");
      return;
    }
    if (!url.trim().toLowerCase().startsWith("https://")) {
      setError("URL must start with https://");
      return;
    }
    if (selected.size === 0) {
      setError("Pick at least one event to subscribe to.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PATCH("/v1/webhook-subscriptions/{id}", {
      params: { path: { id: subscription.id } },
      body: {
        url: url.trim(),
        eventTypes: Array.from(selected) as unknown as never[],
      },
    });
    setSubmitting(false);

    if (apiError) {
      setError("Failed to save changes.");
      return;
    }

    onOpenChange(false);
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>Edit webhook</DialogTitle>
            <DialogDescription>
              Changing the URL or event list takes effect immediately for new events. In-flight
              deliveries (already queued) keep their original target and event list.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="edit-webhook-url">Endpoint URL</Label>
              <Input
                id="edit-webhook-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoFocus
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <Label>Events</Label>
              {/* DialogContent has no max-height: cap the dialog and scroll only the event list, so URL and footer stay reachable. */}
              <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
                {WEBHOOK_EVENT_GROUPS.map((g, i) => {
                  const allChecked = g.events.every((e) => selected.has(e));
                  const someChecked = !allChecked && g.events.some((e) => selected.has(e));
                  const headerLabel = `${allChecked ? "Deselect" : "Select"} all ${g.group} events`;
                  return (
                    <div key={g.group} className={i > 0 ? "border-t border-border" : ""}>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-label={headerLabel}
                        onClick={() => toggleGroup(g.events)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleGroup(g.events);
                          }
                        }}
                        className="flex w-full cursor-pointer select-none items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted/60"
                      >
                        <span className="flex items-center gap-2">
                          <Checkbox
                            checked={allChecked ? true : someChecked ? "indeterminate" : false}
                            onCheckedChange={() => toggleGroup(g.events)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={headerLabel}
                          />
                          {g.group}
                        </span>
                        <span className="font-mono text-[10px]">
                          {g.events.filter((e) => selected.has(e)).length}/{g.events.length}
                        </span>
                      </div>
                      <div className="flex flex-col px-1.5 py-1">
                        {g.events.map((e) => {
                          // htmlFor association rather than wrapping the Checkbox in a <label>, for accessibility.
                          const id = `webhook-event-${e}`;
                          return (
                            <div key={e} className="flex items-center gap-2 rounded-sm px-1.5 py-1 text-sm hover:bg-muted/40">
                              <Checkbox
                                id={id}
                                checked={selected.has(e)}
                                onCheckedChange={() => toggle(e)}
                              />
                              <label htmlFor={id} className="flex-1 cursor-pointer break-all py-0.5 font-mono text-xs">
                                {e}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
